/*
 * THE PARTY TREE. Asked for on 2026-10-01: "Move the respawn under the
 * tree. Make the tree look special".
 *
 * It is the mallorn Sam Gamgee planted in the Party Field after the
 * Scouring, where Bilbo's party tree had been: silver-grey bark and a crown
 * of golden leaves, the only one of its kind west of the Misty Mountains.
 * The respawn is on the flagstones at its foot, so it is the first thing a
 * new character sees and the place everybody comes back to.
 *
 * Two pictures. Flat (partyTreeFlat), it is a tile seen from above, for the
 * flat views — a look at another floor, and anywhere else the ground is
 * laid as tiles: a broad golden crown over the flagstones with the silver
 * limbs showing through it and lanterns hung at its edge. Standing
 * (partyTreePicture), it is stood up on its tile in the oblique view the
 * way every tree is (see treeCut in oblique.ts), only much bigger: a thick
 * silver trunk with roots spread over the paving, limbs forking up into a
 * crown nearly four tiles across, lanterns hanging under it, and a ring
 * of flowers round its foot.
 *
 * It is kept in a file of its own, as the shrub and the chest are, and
 * imports nothing, so neither the sprites nor the renderer have to grow
 * round it.
 */

/** The standing picture's size, in world pixels: four tiles across and nearly five high. */
export const PARTY_TREE_W = 128;
export const PARTY_TREE_H = 152;
/** Where the trunk's foot is in the standing picture, from its top-left. */
export const PARTY_TREE_FOOT = { x: PARTY_TREE_W / 2, y: PARTY_TREE_H - 10 };

/** The crown's golds, darkest first, and the bark's silvers. */
const GOLD = ["#8a5c0e", "#b8821a", "#dca62a", "#f0c840", "#fde58a"];
const SILVER = { dark: "#767c86", mid: "#aeb4bd", light: "#e4e8ec" };
const LANTERN = { glow: "rgba(255,196,96,0.35)", body: "#ffcf6a", core: "#fff6d0" };
const FLOWERS = ["#f4f0f8", "#f2a0c0", "#b0a0f0", "#f8e070"];

type Ctx = CanvasRenderingContext2D;

/** A little dice that rolls the same every time, so the tree is always the same tree. */
function dice(seed: number): () => number {
  let s = seed;
  return () => ((s = (Math.imul(s, 1103515245) + 12345) & 0x7fffffff) / 0x7fffffff);
}

function blob(ctx: Ctx, x: number, y: number, r: number, fill: string) {
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

function lantern(ctx: Ctx, x: number, y: number, s = 1) {
  blob(ctx, x, y, 4.5 * s, LANTERN.glow);
  ctx.fillStyle = LANTERN.body;
  ctx.fillRect(Math.round(x - 1.5 * s), Math.round(y - 2 * s), Math.round(3 * s), Math.round(4 * s));
  ctx.fillStyle = LANTERN.core;
  ctx.fillRect(Math.round(x - 0.5 * s), Math.round(y - 1 * s), Math.max(1, Math.round(s)), Math.max(1, Math.round(2 * s)));
}

/**
 * A crown of leaves: an oval with lobes round its edge, so its outline is
 * leafy rather than drawn with a compass. Traced as one path, to be filled
 * or clipped to.
 */
function crownPath(ctx: Ctx, cx: number, cy: number, rx: number, ry: number, lobe: number, n: number, r: () => number) {
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx - lobe * 0.6, ry - lobe * 0.6, 0, 0, Math.PI * 2);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const x = cx + Math.cos(a) * (rx - lobe), y = cy + Math.sin(a) * (ry - lobe);
    const s = lobe * (0.85 + r() * 0.3);
    ctx.moveTo(x + s, y);
    ctx.arc(x, y, s, 0, Math.PI * 2);
  }
}

/**
 * The flat picture, drawn into a tile-sized context `size` pixels square
 * over the grass it stands in (the caller lays that): the tree from above
 * the way the flat views draw every tree — a crown over a stub of trunk —
 * only gold, and filling the tile, with the silver limbs showing through
 * the leaves and lanterns at the rim.
 */
export function partyTreeFlat(ctx: Ctx, size: number) {
  const k = size / 32;
  const r = dice(1931);
  // The trunk, silver, under the crown's south edge.
  ctx.fillStyle = SILVER.dark;
  ctx.fillRect(Math.round(12 * k), Math.round(20 * k), Math.round(8 * k), Math.round(11 * k));
  ctx.fillStyle = SILVER.light;
  ctx.fillRect(Math.round(13 * k), Math.round(21 * k), Math.max(1, Math.round(2 * k)), Math.round(9 * k));
  // The crown: dark gold underneath, lit gold on top, lobed at the edge.
  ctx.save();
  ctx.translate(0, 0);
  ctx.fillStyle = GOLD[0];
  crownPath(ctx, 16 * k, 14 * k, 16 * k, 14 * k, 4 * k, 9, r);
  ctx.fill();
  ctx.fillStyle = GOLD[1];
  crownPath(ctx, 15.5 * k, 13 * k, 15 * k, 12.5 * k, 4 * k, 9, r);
  ctx.fill();
  ctx.fillStyle = GOLD[2];
  crownPath(ctx, 14.5 * k, 12 * k, 12 * k, 10 * k, 3.5 * k, 8, r);
  ctx.fill();
  ctx.fillStyle = GOLD[3];
  crownPath(ctx, 13 * k, 10.5 * k, 7.5 * k, 6.5 * k, 2.5 * k, 7, r);
  ctx.fill();
  ctx.restore();
  for (let i = 0; i < 16; i++) {
    ctx.fillStyle = r() < 0.5 ? GOLD[4] : GOLD[1];
    ctx.fillRect(Math.round((5 + r() * 22) * k), Math.round((3 + r() * 20) * k), Math.max(1, Math.round(k)), Math.max(1, Math.round(k)));
  }
  // Silver limbs through the leaves.
  ctx.strokeStyle = SILVER.light;
  ctx.lineWidth = Math.max(1, k);
  ctx.beginPath();
  for (const [x0, y0, x1, y1] of [[16, 15, 7, 9], [16, 15, 25, 10], [16, 15, 16, 5]]) {
    ctx.moveTo(x0 * k, y0 * k);
    ctx.lineTo(x1 * k, y1 * k);
  }
  ctx.stroke();
  // Lanterns at the rim.
  for (const [x, y] of [[4, 18], [28, 18], [10, 25], [22, 25]]) lantern(ctx, x * k, y * k, 0.8 * k);
}

let standing: HTMLCanvasElement | null = null;

/**
 * The standing tree, made once and kept: PARTY_TREE_W by PARTY_TREE_H, with
 * the trunk's foot at PARTY_TREE_FOOT, to be put down with that point on
 * the foot of its tile.
 */
export function partyTreePicture(): HTMLCanvasElement {
  if (standing) return standing;
  const c = document.createElement("canvas");
  c.width = PARTY_TREE_W;
  c.height = PARTY_TREE_H;
  // Kept off the graphics card, as keptContext in paper.ts says why.
  const ctx = c.getContext("2d", { willReadFrequently: true })!;
  const r = dice(2026);
  const fx = PARTY_TREE_FOOT.x, fy = PARTY_TREE_FOOT.y;

  // Its shadow on the paving, and the ring of flowers round its foot.
  ctx.fillStyle = "rgba(40,30,10,0.3)";
  ctx.beginPath();
  ctx.ellipse(fx, fy, 30, 9, 0, 0, Math.PI * 2);
  ctx.fill();
  for (let i = 0; i < 30; i++) {
    const a = (i / 30) * Math.PI * 2;
    ctx.fillStyle = FLOWERS[i % FLOWERS.length];
    ctx.fillRect(Math.round(fx + Math.cos(a) * 26 - 1), Math.round(fy + Math.sin(a) * 7.5 - 1), 2, 2);
  }

  // The crown's middle and size: wide, a dome over the field.
  const cx = fx, cy = 50, rx = 62, ry = 44;
  const top = cy + 20; // the trunk runs up into the crown to here

  // The trunk: thick and silver, flaring into roots over the paving,
  // shaded on its east side and lit on its west. Roots first, so the trunk
  // sits on them.
  ctx.fillStyle = SILVER.dark;
  for (const [dx, len, drop] of [[-1, 22, 1], [1, 21, 1], [-1, 13, 4], [1, 14, 4]]) {
    ctx.beginPath();
    ctx.moveTo(fx + dx * 6, fy - 9);
    ctx.quadraticCurveTo(fx + dx * len * 0.7, fy - 3, fx + dx * len, fy + drop);
    ctx.lineTo(fx + dx * 5, fy + 1);
    ctx.closePath();
    ctx.fill();
  }
  const trunk = (w0: number, w1: number, fill: string, shift = 0) => {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(fx - w0 + shift, fy);
    ctx.quadraticCurveTo(fx - w1 - 2 + shift, fy - 24, fx - w1 + shift, top);
    ctx.lineTo(fx + w1 + shift, top);
    ctx.quadraticCurveTo(fx + w1 + 2 + shift, fy - 24, fx + w0 + shift, fy);
    ctx.closePath();
    ctx.fill();
  };
  trunk(15, 9, SILVER.dark);
  trunk(12, 7, SILVER.mid, -1);
  trunk(5, 3, SILVER.light, -6);
  // The mallorn's bark is smooth: a few faint rings rather than furrows.
  ctx.fillStyle = "rgba(90,96,104,0.4)";
  for (let y = top + 14; y < fy - 6; y += 8) ctx.fillRect(fx - 9, y, 18, 1);

  // Limbs forking up out of the trunk into the crown.
  ctx.strokeStyle = SILVER.mid;
  ctx.lineCap = "round";
  for (const [dx, dy, w] of [[-38, -26, 6], [38, -24, 6], [-18, -44, 5], [18, -46, 5], [0, -52, 5]]) {
    ctx.lineWidth = w;
    ctx.beginPath();
    ctx.moveTo(fx, top + 10);
    ctx.quadraticCurveTo(fx + dx * 0.3, top + dy * 0.45, fx + dx, top + dy);
    ctx.stroke();
  }

  // The crown: a broad dome of golden leaf, nearly four tiles across. Its
  // whole shape in the darkest gold first, then lit from the north-west
  // through it, then clumps of leaves catching that light, then single
  // leaves.
  ctx.fillStyle = GOLD[0];
  crownPath(ctx, cx, cy, rx, ry, 13, 16, r);
  ctx.fill();
  ctx.save();
  crownPath(ctx, cx, cy - 2, rx - 3, ry - 4, 12, 16, r);
  ctx.clip();
  const lit = ctx.createRadialGradient(cx - 22, cy - 22, 6, cx - 6, cy - 6, rx + 10);
  lit.addColorStop(0, GOLD[4]);
  lit.addColorStop(0.3, GOLD[3]);
  lit.addColorStop(0.6, GOLD[2]);
  lit.addColorStop(0.85, GOLD[1]);
  lit.addColorStop(1, GOLD[0]);
  ctx.fillStyle = lit;
  ctx.fillRect(0, 0, PARTY_TREE_W, PARTY_TREE_H);
  // Clumps: each a shadow below and a lit cap above, the light falling
  // off away from the north-west.
  for (let i = 0; i < 26; i++) {
    const a = r() * Math.PI * 2, d = Math.sqrt(r());
    const x = cx + Math.cos(a) * d * (rx - 14), y = cy + Math.sin(a) * d * (ry - 12);
    const s = 7 + r() * 5;
    const sun = Math.max(0, Math.min(1, 0.5 - ((x - cx) + (y - cy)) / (rx + ry)));
    ctx.globalAlpha = 0.55;
    blob(ctx, x + 2, y + 3, s, GOLD[sun > 0.5 ? 1 : 0]);
    ctx.globalAlpha = 0.8;
    blob(ctx, x - 1, y - 1, s - 3, GOLD[sun > 0.6 ? 4 : sun > 0.35 ? 3 : 2]);
  }
  ctx.globalAlpha = 1;
  // The underside, in shadow, where the lanterns hang.
  ctx.fillStyle = "rgba(90,50,0,0.35)";
  ctx.beginPath();
  ctx.ellipse(cx + 4, cy + ry - 4, rx - 4, 14, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // Single leaves catching the light all over it.
  for (let i = 0; i < 90; i++) {
    const a = r() * Math.PI * 2, d = Math.sqrt(r());
    const x = cx + Math.cos(a) * d * (rx - 6), y = cy + Math.sin(a) * d * (ry - 6);
    const sun = 0.5 - ((x - cx) + (y - cy)) / (rx + ry);
    ctx.fillStyle = sun > 0.55 ? GOLD[4] : sun > 0.3 ? GOLD[3] : r() < 0.5 ? GOLD[2] : GOLD[1];
    ctx.fillRect(Math.round(x), Math.round(y), 2, 2);
  }
  // A glimpse of the silver limbs through the leaves.
  ctx.strokeStyle = SILVER.light;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  for (const [x0, y0, x1, y1] of [[-30, 22, -46, 12], [28, 24, 44, 14], [-10, 10, -18, -4], [10, 12, 20, -2]]) {
    ctx.moveTo(cx + x0, cy + y0);
    ctx.lineTo(cx + x1, cy + y1);
  }
  ctx.stroke();

  // Lanterns hung under the crown's rim, for the party.
  for (const [dx, dy] of [[-46, 28], [-26, 38], [-6, 42], [16, 41], [36, 35], [52, 24]]) {
    ctx.strokeStyle = "rgba(60,40,10,0.7)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx + dx, cy + dy - 7);
    ctx.lineTo(cx + dx, cy + dy - 3);
    ctx.stroke();
    lantern(ctx, cx + dx, cy + dy, 1.3);
  }
  standing = c;
  return c;
}
